/**
 * The client-root resolve-then-act verb (Unit 3.2 / insurescope-xcmui).
 *
 * Every "create content from the client workspace" entry point (the header
 * Upload, the Library tab's New Folder) must FIRST resolve the client's
 * root library folder (the read-shaped POST; 200 whether it pre-existed
 * or was just created) and only then act on the resolved id: acting at the
 * library root instead would 409 against one-root-per-client when the root
 * exists, or squat the root slot when it doesn't. One hook so the verb and
 * its error surface can't drift between call sites.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
    getApiErrorMessage,
    IApiAxiosError,
    ILibraryFolder,
    resolveClientRootFolder,
} from "@/api";

export interface UseResolveClientRootFolderOptions {
    /** Receives the resolved root folder; set state / open the dialog. */
    onResolved: (folder: ILibraryFolder) => void;
    /** Toast fallback when the API error carries no message. */
    errorFallback: string;
}

export function useResolveClientRootFolder(
    clientId: number,
    { onResolved, errorFallback }: UseResolveClientRootFolderOptions
) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => resolveClientRootFolder(clientId),
        onSuccess: (folder) => {
            // The detail read carries root_folder_id (Unit 3); a resolve may
            // have just materialized it, so the workspace can flip from the
            // no-root state to browse (Unit 7).
            queryClient.invalidateQueries({ queryKey: ["client", clientId] });
            onResolved(folder);
        },
        onError: (err) =>
            toast.error(
                getApiErrorMessage(err as IApiAxiosError, errorFallback)
            ),
    });
}
